import { useMutation } from '@apollo/client'
import { Box, Typography, Tooltip, useTheme, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { OPERATION_SEND_EMAIL_CONFIRMATION } from '../../operations-gql/mutations'
import IconText from 'components/IconText'
import { toast } from 'components/global/CUK/Toast/Toast'
import ActionFake from './components/ActionFake'
import ActionRevoke from './components/ActionRevoke'
import { useLocation } from 'react-router-dom'
import { useNavigate } from 'router'
import { useUser } from 'store'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { CardHeader, CardHeaderTitle, CardHeaderEmailLink } from 'packages/ui/CardPreview'
import { LockedUser, PasswordLocked } from '../../StatusContacts'
import { useState, useMemo } from 'react'
import UserHistoryModal from '../History/components/UserHistoryModal'
import { trackEvent } from 'analytics'

const SendEmailConfirmation = ({ id }) => {
  const [send] = useMutation(OPERATION_SEND_EMAIL_CONFIRMATION, {
    variables: {
      id,
    },
    onCompleted: () => {
      toast.success(t('auth.detail.send_mail_ok_toast'))
      trackEvent('user_confirmation_resent', {
        action_scope: 'item'
      })
    },
    onError: () => {
      toast.error(t('auth.detail.send_mail_ko_toast'))
    },
  })
  const { t } = useTranslation()

  return (
    <CardActionButton
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        width: '32px',
        height: '32px',
      }}
      iconlabel={'paper-plane'}
      tooltiptitle={t('auth.detail.send_mail_button')}
      onClick={send}
    />
  )
}

const Status = ({ color, backgroundColor, text }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        fontSize: '0.6875rem',
        color: color,
        backgroundColor: backgroundColor,
        borderRadius: '50px',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 8px',
        fontWeight: 500,
        height: 24,
      }}
    >
      {text}
    </Box>
  )
}

const Header = ({ contact }) => {
  const { t } = useTranslation()
  const [user] = useUser()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const status = useMemo(() => {
    if (!contact.sync) {
      return (
        <Status
          color="#DC2626"
          backgroundColor="#FEE2E2"
          text={t('administration.card.no_sync')}
        />
      )
    } else if (contact.state === 'locked') {
      return <LockedUser />
    } else if (contact.state === 'password_locked') {
      return <PasswordLocked />
    }
    else if (contact.sync && contact.state === 'active') {
      return (
        <Status
          color="#587F31"
          backgroundColor="#E6F0D7"
          text={t('auth.account_status.activated')}
        />
      )
    } else if (contact.sync && contact.state === 'unconfirmed') {
      return (
        <Status
          color="#D97706"
          backgroundColor="#FEF3C7"
          text={t('auth.account_status.pending')}
        />
      )
    } else if (contact.sync && contact.state === 'deactivated') {
      return (
        <Status
          color="#DC2626"
          backgroundColor="#FEE2E2"
          text={t('auth.account_status.disabled')}
        />
      )
    }
  }, [contact])
  return (
    <>
      <CardHeader gap={'0px'}>
        { status }
        <CardHeaderTitle>
          {contact.first_name} {contact.last_name}
        </CardHeaderTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2x',
          }}
        >
          {contact.title ? (
            <Box sx={{ gridColumnStart: 1 }}>
              <div
                title={contact.title}
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  color: '#7A8096',
                  fontSize: '12px',
                  maxWidth: '230px',
                }}
              >
                {contact.title}
              </div>
            </Box>
          ) : null}
          {contact.email && (
            <Box sx={{ gridColumnStart: 1 }}>
              <CardHeaderEmailLink
                onClick={() => {
                  navigator.clipboard.writeText(contact.email)
                  toast.success(t('auth.admin.copy_email.toast_message'))
                }}
              >
                <Tooltip title={t('auth.admin.copy_email.tooltip')} placement={'left'} arrow>
                  <Stack
                    direction={'row'}
                    gap={.5}
                    alignItems={'center'}
                    sx={{
                      color: '#7A8096',
                      fontSize: '12px',
                    }}
                  >
                    <i className={'fa-light fa-copy'} />
                    {contact.email}
                  </Stack>
                </Tooltip>
              </CardHeaderEmailLink>
            </Box>
          )}
          {contact.mobile_phone && (
            <Box
              sx={{
                color: '#7A8096',
                fontSize: '12px',
                fontWeight: 400,
                fontFamily: 'Poppins',
              }}
            >
              {contact.mobile_phone}
            </Box>
          )}
          {contact.admin && (
            <Box>
              <IconText
                icon="crown"
                color={theme.palette.primary.main}
                iconweight="solid"
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                  }}
                >
                  {t('auth.account_status.internal')}
                </Typography>
              </IconText>
            </Box>
          )}
          {!contact.admin && contact.roles.admin_roles && contact.roles.admin_contact && (
            <Tooltip title={t('auth.admin.role.admin_roles_and_contact_description')} arrow>
              <Box>
                <IconText
                  icon="crown"
                  color={'#587F31'}
                  iconweight="solid"
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                    }}
                  >
                    {t('auth.admin.role.admin_roles_and_contact')}
                  </Typography>
                </IconText>
              </Box>
            </Tooltip>
          )}
          <Box sx={{
            display: 'flex',
            gap: '8px',
            marginTop: '8px',
          }}>
            {(user.roles.admin_fake || user.admin) && contact.state === 'active' && <ActionFake contact={contact} />}
            {(user.admin || (user.roles.admin_roles && user.roles.admin_contact) && !user.roles.readonly_contact) &&
              contact.state === 'active' &&
              <ActionRevoke contact={contact} />
            }
            {contact.sync && contact.state === 'unconfirmed' && (user.admin || !user.roles.readonly_contact) && (
              <SendEmailConfirmation id={contact.center_user_id} />
            )}
            {(user.admin || !user.roles.readonly_contact) && (
              <CardActionButton
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                }}
                iconlabel={contact.state === 'deactivated' ? 'play' : 'pause'}
                tooltiptitle={
                  contact.state === 'deactivated'
                    ? t('auth.activate')
                    : t('auth.desactivate')
                }
                onClick={() => {
                  if (!user.demo_role) {
                    navigate({ pathname: `${location.pathname}/disable-user` })
                  }
                }}
              />
            )}
            {user.internal && (
              <CardActionButton
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                }}
                iconlabel={'list-timeline'}
                tooltiptitle={t('auth.user_history', 'Historique utilisateur')}
                onClick={() => {
                  trackEvent('user_history_opened')
                  setShowHistoryModal(true)
                }}
              />
            )}
          </Box>
        </Box>
      </CardHeader>
      {showHistoryModal && (
        <UserHistoryModal
          userId={contact.center_user_id}
          userName={`${contact.first_name} ${contact.last_name}`}
          onClose={() => setShowHistoryModal(false)}
          isInternal={contact.internal || false}
          sfContactId={contact.sf_id}
          email={contact.email}
        />
      )}
    </>
  )
}

export default Header
